import { useId, useState, type ClipboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';

/**
 * Character limits. A field whose schema declares a `maxLength` stops accepting characters
 * at the limit and says how many are left.
 *
 * The count is in GRAPHEMES — user-perceived characters — because that is what be-tepco
 * counts (`uniseg.GraphemeClusterCount`, PHASE 07): "é" typed as e + combining accent, a
 * flag, a family emoji and an Arabic letter with its harakat are each one character to the
 * server, so each is one here. `Intl.Segmenter` does the segmentation; where it is missing
 * the count falls back to code points, which can only over-count (a cluster is at least one
 * code point), so the counter errs towards "fewer left", never towards a refused save.
 */
const segmenter: Intl.Segmenter | null =
  typeof Intl !== 'undefined' && 'Segmenter' in Intl
    ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
    : null;

export function graphemeLength(s: string): number {
  if (!s) return 0;
  if (!segmenter) return Array.from(s).length;
  return [...segmenter.segment(s)].length;
}

/** How many characters are left before the limit; negative when over. */
export function charsLeft(value: string, limit: number): number {
  return limit - graphemeLength(value);
}

/** The band a count falls in — what colour it shows and when it is announced. */
export type CharBand = 'ok' | 'low' | 'zero' | 'over';

/** Within this many characters of the limit the counter turns amber and speaks. */
export const LOW_THRESHOLD = 10;

export function charBand(left: number): CharBand {
  if (left < 0) return 'over';
  if (left === 0) return 'zero';
  if (left <= LOW_THRESHOLD) return 'low';
  return 'ok';
}

/**
 * The native `maxlength` that makes the browser stop at `limit` graphemes. `maxlength`
 * counts UTF-16 units, so every extra unit already in the value (an emoji's surrogate
 * pair, a combining mark) widens it by the same amount. The browser still allows deleting
 * from an over-limit value, which is what a stored over-long value needs.
 */
export function nativeMaxLength(value: string, limit: number): number {
  return limit + (value.length - graphemeLength(value));
}

/**
 * Would pasting `pasted` over the current selection go past the limit? The browser
 * truncates such a paste at `maxlength`; this tells the field to say so.
 */
export function pasteOverflows(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  pasted: string,
  limit: number,
): boolean {
  const kept = value.slice(0, selectionStart) + value.slice(selectionEnd);
  return graphemeLength(kept + pasted) > limit;
}

export interface CharLimit {
  /** Spread onto the <input>/<textarea>. */
  inputProps: {
    maxLength: number;
    onPaste: (e: ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onBlur: () => void;
  };
  /** The id to add to the control's `aria-describedby`. */
  describedBy: string;
  /** The visible counter (and the paste note), rendered under the control. */
  counter: ReactNode;
}

/**
 * The counter for one text control. Returns null when `limit` is absent, so a caller can
 * use it unconditionally.
 */
export function useCharLimit(value: string, limit: number | undefined): CharLimit | null {
  const id = useId();
  const [pasteCut, setPasteCut] = useState(false);
  if (limit === undefined || limit <= 0) return null;
  const left = charsLeft(value, limit);
  return {
    inputProps: {
      maxLength: nativeMaxLength(value, limit),
      onPaste: (e) => {
        const el = e.currentTarget;
        const pasted = e.clipboardData.getData('text');
        const end = el.value.length;
        if (pasteOverflows(el.value, el.selectionStart ?? end, el.selectionEnd ?? end, pasted, limit)) {
          setPasteCut(true);
        }
      },
      // The note is said once, for the paste that caused it; leaving the field puts it
      // away rather than leaving a stale warning on the form.
      onBlur: () => setPasteCut(false),
    },
    describedBy: id,
    counter: <CharCounter id={id} left={left} limit={limit} pasteCut={pasteCut} />,
  };
}

/**
 * "23 left" · "10 left" in amber · "0 left" · "12 over" in the error colour. The visible
 * text is what `aria-describedby` points at; a separate polite live region speaks only
 * when the count CROSSES into a new band (≤10, 0, over), so a screen reader is not read a
 * number on every keystroke. Arabic uses all six CLDR plural forms.
 */
export function CharCounter({
  id,
  left,
  limit,
  pasteCut,
}: {
  id: string;
  left: number;
  limit: number;
  pasteCut?: boolean;
}) {
  const { t } = useTranslation();
  const band = charBand(left);
  const text = band === 'over' ? t('forms.charsOver', { count: -left }) : t('forms.charsLeft', { count: left });

  // Band changes are derived during render (no effect), so the announcement is set in the
  // same commit as the count that caused it.
  const [lastBand, setLastBand] = useState<CharBand>(band);
  const [announcement, setAnnouncement] = useState('');
  if (band !== lastBand) {
    setLastBand(band);
    setAnnouncement(band === 'ok' ? '' : text);
  }

  return (
    <div className="flex flex-col items-end gap-[2px]">
      <p
        id={id}
        className={cn(
          'font-mono text-label-sm tabular-nums tracking-[0.04em]',
          band === 'ok' && 'text-ink-mute',
          (band === 'low' || band === 'zero') && 'text-signal-amber-ink',
          band === 'over' && 'font-semibold text-signal-error',
        )}
        data-band={band}
      >
        {text}
      </p>
      {pasteCut && (
        <p className="max-w-prose self-stretch text-small text-signal-amber-ink" role="status">
          {t('forms.pasteTruncated', { max: limit })}
        </p>
      )}
      <span className="sr-only" aria-live="polite">
        {announcement}
      </span>
    </div>
  );
}
